import React, { useEffect, useState, useCallback } from 'react';
import {
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
} from '@material-ui/core';
import { FiEdit2, FiPlus, FiTrash } from 'react-icons/fi';
import {
  Column,
  DataGrid,
  FilterRow,
  HeaderFilter,
  Scrolling,
  Grouping,
  Lookup,
} from 'devextreme-react/data-grid';

import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';

import { useControlContent } from '../../../hooks/controlContent';

import ControlList from '../ControlList';
import GridToolbar from '../../../components/GridToolbar';
import GridLoader from '../../../components/GridLoader';

import { Board, BoardContent, BoardHeader } from '../../../components/Board';

import DialogTransferPrice from './DialogTransferPrice';
import DialogTransferPriceEdit from './DialogTransferPriceEdit';
import DialogTransferPriceDelete from './DialogTransferPriceDelete';

import api from '../../../services/api';

export default function TransferPrice() {
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [transferPriceId, setTransferPriceId] = useState(null);
  const [openAddTransferPrice, setOpenAddTransferPrice] = useState(false);
  const [openEditTransferPrice, setOpenEditTransferPrice] = useState(false);
  const [openDeleteTransferPrice, setOpenDeleteTransferPrice] = useState(false);
  const [taxPeriodSource] = useState({
    store: new CustomStore({
      key: 'ID',
      loadMode: 'raw',
      load: async () => {
        const response = await api.get('scheduling/tax-periods');
        const taxPeriodsWithDisplayValue = [];
        response.data.map(taxPeriod => {
          taxPeriodsWithDisplayValue.push({
            ...taxPeriod,
            DisplayValue: `${taxPeriod.Period}Q - ${taxPeriod.Year}`,
          });
        });

        return taxPeriodsWithDisplayValue;
      },
    }),
    paginate: true,
  });

  const { setControlContent, setPageTitle } = useControlContent();

  // Set control menu
  useEffect(() => {
    setPageTitle('Finance');
    setControlContent(<ControlList />);

    const transferPriceStore = new CustomStore({
      key: 'ID',
      loadMode: 'raw',
      load: async () => {
        try {
          setLoading(true);
          const response = await api.get('scheduling/transfer-prices');
          setLoading(false);
          return response.data;
        } catch (err) {
          console.log('load transfer-prices', err);
        }
      },
    });

    const transferPriceDataSource = new DataSource({
      store: transferPriceStore,
      paginate: true,
      reshapeOnPush: true,
    });

    setDataSource(transferPriceDataSource);
  }, [setControlContent, setPageTitle]);

  const commandColumnRender = useCallback(e => {
    return (
      <>
        <Tooltip title="Edit" aria-label="edit" placement="left">
          <IconButton
            aria-label="Edit"
            onClick={() => {
              setTransferPriceId(e.key);
              setOpenEditTransferPrice(true);
            }}
          >
            <FiEdit2 size={18} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete" aria-label="delete" placement="left">
          <IconButton
            aria-label="Delete"
            onClick={() => {
              setTransferPriceId(e.key);
              setOpenDeleteTransferPrice(true);
            }}
          >
            <FiTrash size={18} />
          </IconButton>
        </Tooltip>
      </>
    );
  }, []);

  const handleAdd = useCallback(
    data => {
      dataSource.reload();
    },
    [dataSource],
  );

  const handleEdit = useCallback(
    data => {
      dataSource.reload();
    },
    [dataSource],
  );

  return (
    <Board>
      <BoardHeader
        title="Transfer price"
        description="Create, edit and delete transfer prices"
      />

      <BoardContent style={{ padding: 0, margin: 0, position: 'relative' }}>
        <GridToolbar style={{ justifyContent: 'flex-end' }}>
          <Tooltip title="Add new transfer price">
            <Button
              color="primary"
              variant="outlined"
              size="small"
              aria-label="Add new transfer price"
              onClick={() => setOpenAddTransferPrice(true)}
              startIcon={<FiPlus />}
            >
              Add
            </Button>
          </Tooltip>
        </GridToolbar>

        {loading ? (
          <GridLoader>
            <CircularProgress color="primary" />
          </GridLoader>
        ) : null}

        <DataGrid
          dataSource={dataSource}
          showBorders={false}
          remoteOperations={false}
          key="ID"
          height="100%"
        >
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <Scrolling mode="infinite" />
          <Grouping autoExpandAll={false} />

          <Column dataField="Origin.Name" />
          <Column dataField="Destination.Name" />
          <Column dataField="Aliquot" format="#0,00%" />
          <Column dataField="TaxPeriod.ID" caption="Tax Period">
            <Lookup
              dataSource={taxPeriodSource}
              displayExpr="DisplayValue"
              valueExpr="ID"
            />
          </Column>

          <Column
            type="buttons"
            cellRender={commandColumnRender}
            width={100}
          ></Column>
        </DataGrid>

        {openAddTransferPrice ? (
          <DialogTransferPrice
            open={openAddTransferPrice}
            handleOpen={() => setOpenAddTransferPrice(true)}
            handleClose={() => setOpenAddTransferPrice(false)}
            callback={handleAdd}
          />
        ) : null}

        {openEditTransferPrice ? (
          <DialogTransferPriceEdit
            open={openEditTransferPrice}
            handleOpen={() => setOpenEditTransferPrice(true)}
            handleClose={() => setOpenEditTransferPrice(false)}
            callback={handleEdit}
            id={transferPriceId}
          />
        ) : null}

        {openDeleteTransferPrice ? (
          <DialogTransferPriceDelete
            open={openDeleteTransferPrice}
            handleOpen={() => setOpenDeleteTransferPrice(true)}
            handleClose={() => setOpenDeleteTransferPrice(false)}
            callback={handleEdit}
            id={transferPriceId}
          />
        ) : null}
      </BoardContent>
    </Board>
  );
}
